/* ==========================================================================
   Barronia — Slide Cart drawer
   --------------------------------------------------------------------------
   WHAT WAS BROKEN
   ---------------
   Shopify regenerates a line-item key every time that line's quantity
   changes, because the automatic/combo discount allocation is baked into the
   key hash. Verified against the live cart:

       qty 2 -> 3   key 47334703825090:97cb7197...  becomes  ...:19e6d565...
       qty 3 -> 1   key                             becomes  ...:22fadf71...

   The old code deliberately skipped the drawer re-render after a +/- change
   ("update subtotal silently, no full re-render"), so the DOM kept the dead
   key. The next click sent it to /cart/change.js and Shopify answered

       HTTP 400 {"description":"no valid id or line parameter"}

   The failure branch then un-hid the row it had already faded out, which is
   the "item is removed, then 1 second later it comes back" bug. Removing an
   item WITHOUT touching its quantity first still worked — hence "sometimes".

   HOW IT IS FIXED
   ---------------
   1. Every successful cart mutation re-renders the drawer from the sections
      the same response returned, so a key can never go stale. This is the
      actual fix; everything below is belt-and-braces.
   2. If a key is rejected anyway, the line is re-resolved against a fresh
      /cart.js and the change is retried by line number instead of failing.
   3. Failures resync from the server instead of reverting to a guess, so the
      drawer can never disagree with the real cart.
   4. Mutations are serialised. Two overlapping requests were what shifted
      line numbers and invalidated keys mid-flight.
   5. Every control is bound by delegation on `document`, so replacing the
      drawer's innerHTML can never leave a dead button behind, and nothing
      depends on the custom element upgrading. (The previous attempt bound
      inside connectedCallback and also needed a matching edit to
      snippets/cart-item.liquid; when only one of the two files went live the
      drawer stopped opening. This file is self-contained — no other file in
      the theme has to change.)
   ========================================================================== */

(function () {
  'use strict';

  var QTY_DEBOUNCE = 350;

  /* ---- Element lookup ----------------------------------------------------
     Prefer the Slide Cart. Falling back to a bare `cart-drawer` would find a
     leftover Dawn drawer and open something invisible. */
  function drawerEl() {
    return document.querySelector('cart-drawer.sg-cart-drawer');
  }

  function routeCart() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }
  function routeChange() {
    return (window.routes && window.routes.cart_change_url) || '/cart/change';
  }
  function routeAdd() {
    return (window.routes && window.routes.cart_add_url) || '/cart/add';
  }

  /* ---- Open / close ------------------------------------------------------ */

  var scrollY = 0;
  var scrollLocked = false;

  function openDrawer() {
    var el = drawerEl();
    if (!el) return;

    // Only capture the scroll position on the first open. Opening an already
    // open drawer used to record 0 (body is position:fixed by then) and
    // closing it jumped the shopper to the top of the page.
    if (!scrollLocked) {
      scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      scrollLocked = true;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollY + 'px';
      document.body.style.width = '100%';
    }

    el.classList.add('is-active');
    var overlay = document.getElementById('sg-overlay');
    if (overlay) overlay.classList.add('is-active');
  }

  function closeDrawer() {
    var drawers = document.querySelectorAll('cart-drawer');
    for (var i = 0; i < drawers.length; i++) drawers[i].classList.remove('is-active');

    var overlay = document.getElementById('sg-overlay');
    if (overlay) overlay.classList.remove('is-active');

    if (scrollLocked) {
      scrollLocked = false;
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      window.scrollTo(0, scrollY);
    }
  }

  function setLoading(on) {
    var el = drawerEl();
    if (el) el.classList.toggle('sg-loading', !!on);
  }

  /* ---- Rendering ---------------------------------------------------------
     innerHTML never executes <script>, so swapping in the section markup —
     which carries its own <script src="cart-drawer.js"> tag — cannot reload
     this file. */

  function renderSections(parsed) {
    if (!parsed || !parsed.sections) return;

    if (parsed.sections['cart-drawer']) {
      var el = drawerEl();
      if (el) {
        var wrapBefore = el.querySelector('.sg-cart-drawer__items-wrapper');
        var prevScroll = wrapBefore ? wrapBefore.scrollTop : 0;

        var tmp = document.createElement('div');
        tmp.innerHTML = parsed.sections['cart-drawer'];
        var fresh = tmp.querySelector('cart-drawer');
        if (fresh) {
          el.innerHTML = fresh.innerHTML;
          var wrapAfter = el.querySelector('.sg-cart-drawer__items-wrapper');
          if (wrapAfter) wrapAfter.scrollTop = prevScroll;
        }
      }
    }

    if (parsed.sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = parsed.sections['cart-icon-bubble'];
    }
  }

  function fetchCart() {
    setLoading(true);
    return fetch(routeCart() + '?sections=cart-drawer,cart-icon-bubble', {
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart fetch ' + res.status);
        return res.json();
      })
      .then(function (parsed) {
        renderSections({ sections: parsed });
      })
      .catch(function (err) {
        console.error('[sg-cart] fetchCart failed:', err);
      })
      .then(function () {
        setLoading(false);
      });
  }

  /* ---- Line addressing ---------------------------------------------------
     The drawer renders cart.items in cart order, so the row's position among
     .sg-cart-item IS its 1-based line number. Verified against the live cart.
     This is the fallback used when a key has been rejected. */

  function lineOf(itemEl) {
    var el = drawerEl();
    if (!el || !itemEl) return 0;
    var rows = el.querySelectorAll('.sg-cart-item');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] === itemEl) return i + 1;
    }
    return 0;
  }

  function collapse(itemEl) {
    if (!itemEl) return;
    itemEl.style.transition = 'opacity 0.2s ease, max-height 0.3s ease, margin 0.3s ease';
    itemEl.style.overflow = 'hidden';
    itemEl.style.opacity = '0';
    itemEl.style.maxHeight = '0';
    itemEl.style.marginBottom = '0';
  }

  /* ---- The cart mutation ------------------------------------------------- */

  var timers = {};
  var inFlight = false;
  var queued = null;

  function postChange(identifier, quantity) {
    var body = {
      quantity: quantity,
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname,
    };
    if (identifier.id) body.id = identifier.id;
    else body.line = identifier.line;

    return fetch(routeChange() + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /* Given a key Shopify just rejected, find where that line actually sits
     now. Matches on the full key first, then on the variant id. */
  function resolveLine(staleKey) {
    return fetch(routeCart() + '.js', { headers: { 'Cache-Control': 'no-cache' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return 0;
        var i;
        for (i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === staleKey) return i + 1;
        }
        var variantId = String(staleKey).split(':')[0];
        for (i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].variant_id) === variantId) return i + 1;
        }
        return 0;
      })
      .catch(function () {
        return 0;
      });
  }

  /**
   * @param {HTMLElement} itemEl   the .sg-cart-item row acted on
   * @param {number}      quantity absolute target quantity (0 = remove)
   * @param {boolean}     now      skip the debounce (removals)
   */
  function updateQty(itemEl, quantity, now) {
    if (!itemEl) return;

    var key = itemEl.getAttribute('data-key') || '';
    var line = lineOf(itemEl);

    if (!key && !line) {
      console.error('[sg-cart] cannot identify cart line; resyncing.');
      fetchCart();
      return;
    }

    var slot = key || 'line-' + line;
    clearTimeout(timers[slot]);

    var job = { key: key, line: line, quantity: quantity, itemEl: itemEl, slot: slot };

    if (now) {
      commit(job);
    } else {
      timers[slot] = setTimeout(function () {
        commit(job);
      }, QTY_DEBOUNCE);
    }
  }

  function commit(job) {
    // Never let two mutations overlap. Overlapping requests are what shift
    // line numbers and rotate keys underneath each other.
    if (inFlight) {
      queued = job;
      return;
    }
    inFlight = true;
    setLoading(true);

    // Recompute the line at send time — the DOM may have changed since the
    // click that scheduled this job.
    var line = lineOf(job.itemEl) || job.line;
    var first = job.key ? { id: job.key } : { line: line };

    postChange(first, job.quantity)
      .then(function (res) {
        if (res.ok) return res;

        // Stale key. Re-resolve against the live cart and retry by line.
        if (job.key) {
          return resolveLine(job.key).then(function (freshLine) {
            var target = freshLine || line;
            if (!target) return res;
            return postChange({ line: target }, job.quantity);
          });
        }
        return res;
      })
      .then(function (res) {
        if (!res.ok) {
          return res
            .clone()
            .json()
            .catch(function () {
              return {};
            })
            .then(function (info) {
              console.error(
                '[sg-cart] /cart/change.js ' + res.status + ' ' + (info.description || '')
              );
              // Show the shopper the real cart, never a half-updated guess.
              return fetchCart();
            });
        }

        return res.json().then(function (parsed) {
          // A queued click is still pending and the shopper is looking at the
          // optimistic DOM it was measured against — let that one land first.
          if (queued) return;

          if (job.quantity === 0) {
            return new Promise(function (resolve) {
              setTimeout(function () {
                renderSections(parsed);
                resolve();
              }, 220); // let the collapse animation finish
            });
          }
          renderSections(parsed);
        });
      })
      .catch(function (err) {
        console.error('[sg-cart] update failed:', err);
        return fetchCart();
      })
      .then(function () {
        inFlight = false;
        setLoading(false);
        var next = queued;
        queued = null;
        if (next) commit(next);
      });
  }

  /* ---- Adding ------------------------------------------------------------ */

  function addVariant(variantId, btn) {
    setLoading(true);
    var label = btn ? btn.textContent : '';
    if (btn) btn.textContent = 'Adding...';

    return fetch(routeAdd() + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) {
        if (!res.ok) return fetchCart();
        return res.json().then(function (parsed) {
          renderSections(parsed);
          openDrawer();
        });
      })
      .catch(function (err) {
        console.error('[sg-cart] add failed:', err);
        return fetchCart();
      })
      .then(function () {
        if (btn) btn.textContent = label || 'Add';
        setLoading(false);
      });
  }

  /* ---- Delegated event handling -----------------------------------------
     Everything hangs off `document`, so no handler is ever lost when the
     drawer's innerHTML is replaced, and nothing here depends on the
     <cart-drawer> custom element having upgraded. */

  function bindOnce() {
    if (window.__sgCartBound) return;
    window.__sgCartBound = true;

    document.addEventListener(
      'click',
      function (e) {
        var t = e.target;
        if (!t || !t.closest) return;

        /* --- open the drawer --- */
        if (t.closest('#cart-icon-bubble') || t.closest('[data-cart-toggle]')) {
          if (drawerEl()) {
            e.preventDefault();
            openDrawer();
          }
          return;
        }

        /* --- close the drawer --- */
        if (t.closest('.sg-cart-drawer__close') || t.closest('.sg-cart-drawer__continue')) {
          e.preventDefault();
          closeDrawer();
          return;
        }
        if (t.id === 'sg-overlay' || t.closest('#sg-overlay')) {
          var ov = drawerEl();
          if (!ov || ov.getAttribute('data-click-to-close') !== 'false') closeDrawer();
          return;
        }

        /* --- checkout --- */
        if (t.closest('.sg-cart-drawer__checkout')) {
          e.preventDefault();
          window.location.href = '/checkout';
          return;
        }

        /* --- quantity: minus --- */
        var minus = t.closest('.sg-cart-item__qty-btn--minus');
        if (minus) {
          e.preventDefault();
          var mRow = minus.closest('.sg-cart-item');
          var mInput = mRow && mRow.querySelector('.sg-cart-item__qty-input');
          if (!mRow || !mInput) return;

          var mVal = parseInt(mInput.value, 10) || 1;
          if (mVal > 1) {
            mInput.value = mVal - 1;
            updateQty(mRow, mVal - 1, false);
          } else {
            collapse(mRow);
            updateQty(mRow, 0, true);
          }
          return;
        }

        /* --- quantity: plus --- */
        var plus = t.closest('.sg-cart-item__qty-btn--plus');
        if (plus) {
          e.preventDefault();
          var pRow = plus.closest('.sg-cart-item');
          var pInput = pRow && pRow.querySelector('.sg-cart-item__qty-input');
          if (!pRow || !pInput) return;

          var pVal = parseInt(pInput.value, 10) || 0;
          pInput.value = pVal + 1;
          updateQty(pRow, pVal + 1, false);
          return;
        }

        /* --- remove --- */
        var rm = t.closest('.sg-cart-item__remove');
        if (rm) {
          e.preventDefault();
          var rRow = rm.closest('.sg-cart-item');
          if (!rRow) return;
          collapse(rRow);
          updateQty(rRow, 0, true);
          return;
        }

        /* --- "often bought together" add --- */
        var rec = t.closest('.sg-rec-add');
        if (rec) {
          e.preventDefault();
          var vid = rec.getAttribute('data-variant');
          if (vid) addVariant(vid, rec);
          return;
        }

        /* --- plain /cart/add links --- */
        var link = t.closest('a[href*="/cart/add"]');
        if (link && drawerEl()) {
          var url;
          try {
            url = new URL(link.href, window.location.origin);
          } catch (_) {
            return;
          }
          var id = url.searchParams.get('id');
          if (!id) return;

          e.preventDefault();
          var prev = link.innerHTML;
          link.innerHTML = 'Adding...';
          setLoading(true);

          fetch(routeAdd() + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: id,
              quantity: url.searchParams.get('quantity') || 1,
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname,
            }),
          })
            .then(function (res) {
              return res.ok ? res.json() : null;
            })
            .then(function (parsed) {
              if (parsed) {
                renderSections(parsed);
                openDrawer();
              } else {
                return fetchCart().then(openDrawer);
              }
            })
            .catch(function (err) {
              console.error('[sg-cart]', err);
            })
            .then(function () {
              link.innerHTML = prev;
              setLoading(false);
            });
          return;
        }

        /* --- backdrop: inside <cart-drawer> but outside the panel.
               Checked last so it can never swallow a control click. --- */
        if (t.closest('cart-drawer.sg-cart-drawer') && !t.closest('.sg-cart-drawer__inner')) {
          closeDrawer();
        }
      },
      false
    );

    /* --- typed quantity --- */
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.classList || !input.classList.contains('sg-cart-item__qty-input')) return;

      var row = input.closest('.sg-cart-item');
      var val = parseInt(input.value, 10);
      if (!row || isNaN(val) || val < 0) return;

      if (val === 0) collapse(row);
      updateQty(row, val, val === 0);
    });

    /* --- escape closes --- */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var el = drawerEl();
      if (el && el.classList.contains('is-active')) closeDrawer();
    });

    /* --- theme / app events --------------------------------------------
       barronia-featured-collection.liquid (the card "Add to Cart") fires
       cart:refresh with the parsed state it already has. */
    document.addEventListener('cart:refresh', function (e) {
      if (!drawerEl()) return;
      if (e.detail && e.detail.parsedState) renderSections(e.detail.parsedState);
      else fetchCart();
      openDrawer();
    });

    document.addEventListener('cart:updated', function () {
      if (drawerEl()) fetchCart();
    });

    /* --- any <form action="/cart/add"> ---
       preventDefault only AFTER we know a drawer exists to render into,
       otherwise a missing drawer would leave a completely dead button. */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.getAttribute) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (!drawerEl()) return;

      e.preventDefault();

      var submitBtn = form.querySelector('[type="submit"]') || form.querySelector('button');
      var originalText = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) submitBtn.innerHTML = 'Adding...';

      var formData = new FormData(form);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      fetch(routeAdd() + '.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (res) {
          return res.json().then(function (parsed) {
            return { ok: res.ok, parsed: parsed };
          });
        })
        .then(function (out) {
          if (out.ok) {
            renderSections(out.parsed);
            openDrawer();
          } else {
            console.error('[sg-cart] add failed:', out.parsed);
            alert(out.parsed.description || 'Sorry, there was an issue adding to cart.');
          }
        })
        .catch(function (err) {
          console.error('[sg-cart]', err);
        })
        .then(function () {
          if (submitBtn) submitBtn.innerHTML = originalText;
        });
    });
  }

  /* ---- Custom element ----------------------------------------------------
     Kept so Dawn's product-form.js still finds a cart object to talk to, and
     so the section's inline
        onclick="document.querySelector('cart-drawer').close()"
     keeps working. All real behaviour lives in the delegated listeners above,
     so the drawer still works even if another script wins the tag name. */

  class SGCartDrawer extends HTMLElement {
    connectedCallback() {
      bindOnce();
    }
    open() {
      openDrawer();
    }
    close() {
      closeDrawer();
    }
    fetchCart() {
      return fetchCart();
    }
    renderSections(parsed) {
      renderSections(parsed);
    }
    getSectionsToRender() {
      return [];
    }
    setActiveElement() {}
    renderContents(parsed) {
      if (parsed && parsed.sections) renderSections(parsed);
      else fetchCart();
      openDrawer();
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', SGCartDrawer);
  }

  // Bind immediately as well, so the drawer works even if the custom element
  // never upgrades (another script already owning the <cart-drawer> tag).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOnce);
  } else {
    bindOnce();
  }
})();
